import { B2cProductSearchPayload } from "@/generated/graphql/b2c";
import { AreaFilters, isFiltersEmpty, SortValue } from "./area-filters";
import { addType, createBrandOptions, createCategoryOptions, createSorterOptions, filterOptions, hasType, MinimalB2cBrand, MinimalB2cCategory, Option, removeType } from "./options";
import { StoreSync } from "./sync";
import { Debouncer } from "./debouncer";
import { Url } from "@/helpers/url";
import { Services } from "@/services/services";

export type Key = 'main' | 'filters' | 'sorter';

export type B2cProduct = B2cProductSearchPayload;

type ValueState = {
    selections: Set<string>;
    sort: null | SortValue;
    page: null | number;
    search: null | string;
    price: {
        from: string;
        to: string;
    };
};

type CommonGroupState = ValueState & {
    searchs: Record<string, string>;
    options: Record<string, Array<Option>>;
};

type MainGroup = CommonGroupState & {
    _type: 'main';
};

type FiltersGroup = CommonGroupState & {
    _type: 'filters';
};

type SorterGroup = CommonGroupState & {
    _type: 'sorter';
};

export type FiltersChangeHandler = (filters: AreaFilters) => void;

export class AreaStore {
    debouncer: Debouncer;
    sync: StoreSync

    filters!: AreaFilters;
    onFiltersChange!: FiltersChangeHandler;

    groups: {
        main?: null | MainGroup;
        filters?: null | FiltersGroup;
        sorter?: null | SorterGroup;
    }
    options: Record<string, Array<{ key: string, name: string, _search: string }>> = {};
    products: B2cProduct[] = [];
    more: boolean = false;
    popup: "filters" | "sorter" | null = null;
    totalItems: number = 0;

    constructor(protected breadcrumbs: Services['breadcrumbs']) {
        this.debouncer = new Debouncer(700);
        this.sync = new StoreSync();
        this.groups = {};
    }

    setParams = (params: {
        filters: AreaFilters,
        brands: MinimalB2cBrand[];
        categories: MinimalB2cCategory[];
        products: B2cProduct[];
        more: boolean;
        onFiltersChange: FiltersChangeHandler;
        totalItems: number;
    }) => {
        this.filters = params.filters;
        this.options = {
            brands: createBrandOptions(params.brands),
            categories: createCategoryOptions(params.categories),
            sorters: createSorterOptions()
        };
        this.products = params.products;
        this.more = params.more;
        this.onFiltersChange = params.onFiltersChange;
        this.totalItems = params.totalItems;

        this.updateGroups();
    }

    updateGroups = () => {
        const keys = Object.keys(this.groups) as Array<'main' | 'filters' | 'sorter'>;
        if (!keys.includes('main')) {
            keys.push('main');
        }

        keys.forEach((group) => {
            this.fillGroup(group);
            this.fillGroupValues(this.groups[group]!, this.filters);
            this.fillGroupState(this.groups[group]!);
        });
    }

    getFilteredOptions = (group: Key, key: string) => {
        return filterOptions(
            this.options[key] || [],
            this.groups[group]!.searchs[key] || ''
        );
    }

    release = () => {
        this.debouncer.clear();
    }

    sorterPopupApply = (sort: SortValue) => {
        this.changeSort('main', sort);
        this.closePopup('sorter');
    }

    toggleSelection = (group: Key, selection: string) => {
        const selections = this.groups[group]!.selections;

        if (selections.has(selection)) {
            selections.delete(selection);
        } else {
            selections.add(selection);
        }

        this.commitGroup(group, group === 'main' ? 'debounced' : 'none');
    }

    removeSelection = (group: Key, selection: string) => {
        const selections = this.groups[group]!.selections;

        if (selections.has(selection)) {
            selections.delete(selection);
        }

        this.commitGroup(group, 'immediate');
    }

    changePrice = (group: Key, type: 'from' | 'to', value: string, input: HTMLInputElement) => {
        const isPositive = /^\d+\.?\d*$/.test(value);
        const isZero = value === '0';

        if (!isPositive || isZero) {
            value = '';
            input.value = value;
        }

        const price = this.groups[group]!.price;
        price[type] = value;

        this.commitGroup(group, group === 'main' ? 'debounced' : 'none');
    }

    changeSearch = (group: Key, type: string, search: string) => {
        const searchs = this.groups[group]!.searchs;

        searchs[type] = search;

        this.commitGroup(group, 'none');
    }

    changeSort = (group: Key, sort: SortValue) => {
        this.groups[group]!.sort = sort;

        this.commitGroup(group, group === 'main' ? 'immediate' : 'none');
    }

    applyClearButton = (group: Key) => {
        if (group === 'main') {
            this.clearFilters();
            this.fillGroupState(this.groups[group]!);
            this.fillGroupValues(this.groups[group]!, this.filters);

            this.commitGroup(group, 'immediate');
        }
    }

    clearFilters = () => {
        this.filters.brands = [];
        this.filters.categories = [];
        this.filters.priceMin = null;
        this.filters.priceMax = null;
    }

    applySubmitButton = (group: Key) => {
        if (group === 'main') {
            this.emitResult(group, true);
        }
    }

    commitGroup = (group: Key, emit: 'none' | 'debounced' | 'immediate' = 'none', resetPage: boolean = true) => {
        if (group === 'main' && resetPage) {
            if (!this.groups.main) {
                this.groups.main = this.createMainGroup();
            }

            this.groups.main.page = 1;
        }

        this.notifyUpdate();

        if (emit !== 'none') {
            this.emitResult(group, emit === 'immediate');
        }
    }

    notifyUpdate = () => {
        this.sync.notify();
    }

    emitResult = (group: Key, immediate: boolean) => {
        this.debouncer.run(() => {
            const state = this.groups[group]!;
            const filters = this.getFilters(state);

            this.onFiltersChange(filters);
        }, { immediate });
    }

    applyPage = (page: number) => {
        if (!Number.isFinite(page)) return;
        const normalized = Math.max(1, Math.floor(page));

        if (!this.groups.main) this.groups.main = this.createMainGroup();
        const group = this.groups.main!;
        if (group.page === normalized) return;

        group.page = normalized;
        this.commitGroup('main', 'immediate', false);
    };

    applyBackToSearchButton = () => {
        const empty = isFiltersEmpty(this.filters);

        if (empty || true) {
            Url.route(this.breadcrumbs.backUrl);
        } else {
            this.applyClearButton('main');
        }
    }

    closeFiltersPopup = () => {
        this.closePopup('filters');
    }

    closeSorterPopup = () => {
        this.closePopup('sorter');
    }

    applyFiltersPopup = () => {
        this.applyPopup('filters');
    }

    applySorterPopup = () => {
        this.applyPopup('sorter');
    }

    applyPopup = (group: Key) => {
        this.popup = null;

        if (!this.groups.main) {
            this.groups.main = this.createMainGroup();
        }

        this.groups.main.page = 1;
        if (this.groups[group]) {
            this.groups[group]!.page = 1;
        }

        this.commitGroup(group, 'immediate', false);
    };

    closePopup = (group: Key) => {
        this.popup = null;
        this.groups[group] = null;

        this.notifyUpdate();
    }

    openFiltersPopup = () => {
        this.groups.filters = this.createFiltersGroup();
        this.popup = 'filters';

        this.fillGroupValues(this.groups.filters, this.filters);
        this.fillGroupState(this.groups.filters);
        this.notifyUpdate();
    }

    openSorterPopup = () => {
        this.groups.sorter = this.createSorterGroup();
        this.popup = 'sorter';

        this.fillGroupValues(this.groups.sorter, this.filters);
        this.fillGroupState(this.groups.sorter);
        this.notifyUpdate();
    }

    clearFiltersPopup = () => {
        this.fillGroupValues(this.groups.filters!, this.filters);
        this.fillGroupState(this.groups.filters!);

        this.notifyUpdate();
    }

    createMainGroup = (): MainGroup => {
        return {
            _type: 'main' as const,
            selections: new Set(),
            sort: null,
            page: null,
            search: null,
            price: {
                from: '',
                to: ''
            },
            searchs: {},
            options: {
                brands: [],
                categories: [],
                sorters: []
            },
        };
    }

    createSorterGroup = (): SorterGroup => {
        return {
            _type: 'sorter' as const,
            selections: new Set(),
            sort: null,
            page: null,
            search: null,
            price: {
                from: '',
                to: ''
            },
            searchs: {},
            options: {
                brands: [],
                categories: [],
                sorters: []
            },
        };
    }

    createFiltersGroup = (): FiltersGroup => {
        return {
            _type: 'filters' as const,
            selections: new Set(),
            sort: null,
            page: null,
            search: null,
            price: {
                from: '',
                to: ''
            },
            searchs: {},
            options: {
                brands: [],
                categories: [],
                sorters: []
            },
        };
    }

    fillGroup = (groupKey: Key) => {
        switch (groupKey) {
            case 'main':
                this.groups[groupKey] = this.createMainGroup();
                break;
            case 'filters':
                this.groups[groupKey] = this.createFiltersGroup();
                break;
            case 'sorter':
                this.groups[groupKey] = this.createSorterGroup();
                break;
        }
    }

    fillGroupValues = (group: ValueState, filters: AreaFilters) => {
        group.selections.clear();

        filters.brands.forEach((brand) => {
            group.selections.add(addType('brands', brand));
        });
        filters.categories.forEach((category) => {
            group.selections.add(addType('categories', category));
        });

        group.sort = filters.sort;
        group.page = filters.page;
        group.search = filters.search;

        group.price.from = filters.priceMin?.toString() ?? '';
        group.price.to = filters.priceMax?.toString() ?? '';
    }

    fillGroupState = (group: CommonGroupState) => {
        group.searchs = {};

        group.options.brands = this.options.brands;
        group.options.categories = this.options.categories;
        group.options.sorters = this.options.sorters;
    }

    getFilters = (group: CommonGroupState): AreaFilters => {
        const selections = [...group.selections];

        const params: AreaFilters = {
            brands: [],
            categories: [],
            priceMin: group.price.from ? parseFloat(group.price.from) : null,
            priceMax: group.price.to ? parseFloat(group.price.to) : null,
            sort: group.sort,
            page: group.page,
            search: group.search
        };

        (['brands', 'categories'] as const).forEach((type) => {
            params[type] = selections.reduce((acc, key) => {
                if (hasType(type, key)) {
                    acc.push(removeType(type, key));
                }

                return acc;
            }, [] as string[]);
        });

        return params;
    }
}